import { useState, type FormEvent } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { IS_STAGING } from "../utils/environment";
import { EMULATOR_TEST_USER, USE_EMULATORS } from "../utils/emulators";

/**
 * Email/password sign-in against the Auth emulator, for local runs and the
 * e2e smoke test, where there is no Google account to pop up. Only rendered
 * when USE_EMULATORS, which is `false` in every non-emulator build, so this is
 * compiled out of production. Prefilled with the account the seed script
 * creates (scripts/seed-emulators.ts).
 */
function EmulatorSignIn() {
  const [email, setEmail] = useState<string>(EMULATOR_TEST_USER.email);
  const [password, setPassword] = useState<string>(EMULATOR_TEST_USER.password);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <form className="login-emulator" onSubmit={submit} data-testid="emulator-sign-in">
      <p className="login-fineprint">Emulator mode — sign in with a seeded test account.</p>
      <input
        type="email"
        aria-label="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="username"
      />
      <input
        type="password"
        aria-label="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
      />
      <button type="submit" className="btn btn-block btn-secondary" disabled={busy}>
        {busy ? "Signing in…" : "Sign in (emulator)"}
      </button>
      {error && <p className="login-fineprint" role="alert">{error}</p>}
    </form>
  );
}

export function Login() {
  const { signInWithGoogle } = useAuth();

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error("Sign in failed:", error);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-mark" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 21s-7-5.6-7-11a7 7 0 0 1 14 0c0 5.4-7 11-7 11Z" />
            <circle cx="12" cy="10" r="2.4" />
          </svg>
        </div>
        <h1 className="login-title">Trip Tracker</h1>
        {IS_STAGING && <span className="env-badge">Staging</span>}
        <p className="login-sub">
          Your travel log — from line to lounge. Track every trip, from theme parks to
          cruises and everywhere between.
        </p>
        <button onClick={handleSignIn} className="btn btn-block login-google">
          Sign in with Google
        </button>
        <p className="login-fineprint">Use your Google account to get started.</p>
        {USE_EMULATORS && <EmulatorSignIn />}
      </div>
    </div>
  );
}
